import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { BentoBrand } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";

// Matches BentoBrandFlagReason in the backend
const OPTIONS: string[] = [
  "Wrong Category",
  "Incorrect brand description",
  "Wrong or inaccurate social link(s)",
  "Wrong location",
  "Inactive company / no website",
  "Bad contact",
  "Other",
];

interface IProps {
  open: boolean;
  close: () => void;
  brand: BentoBrand | null;
}

export function ReportWrongInfoDialog({ open, close, brand }: IProps) {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);

  const [feedbacks, setFeedbacks] = useState<string[]>([]);
  const [comment, setComment] = useState<string>("");

  const handleFeedbackChange = (value: string) => {
    setFeedbacks((prevFeedbacks) => {
      if (prevFeedbacks.includes(value)) {
        return prevFeedbacks.filter((feedback) => feedback !== value);
      } else {
        return [...prevFeedbacks, value];
      }
    });
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment((event.target as HTMLInputElement).value);
  };

  const submitForm = async () => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-brands/${brand?.id}/feedback`,
        "POST",
        {},
        {
          reasons: feedbacks,
          comment,
        },
      );
      setAlert("Thank you for the feedback!", "success");
      handleClearField();
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleClearField = () => {
    setFeedbacks([]);
    setComment("");
    close();
  };

  return (
    <Dialog open={open} onClose={handleClearField}>
      <DialogTitle>
        Report Inaccurate Information about {brand?.normalizedName}:
      </DialogTitle>
      <DialogContent>
        <FormControl sx={{ mb: 2 }}>
          {OPTIONS.map((option) => (
            <Box key={option}>
              <FormControlLabel
                onChange={() => handleFeedbackChange(option)}
                control={<Checkbox checked={feedbacks.includes(option)} />}
                label={option}
              />
            </Box>
          ))}
        </FormControl>

        {feedbacks?.includes("Other") && (
          <TextField
            variant="outlined"
            name="additionalFeedback"
            placeholder="Any additional feedback?"
            fullWidth
            multiline
            minRows={4}
            value={comment}
            onChange={handleCommentChange}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClearField}>
          Cancel
        </Button>
        <Button onClick={submitForm}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
}
